import { useEffect, useMemo, useState } from 'react';
import usePaperAttemptStatuses from './usePaperAttemptStatuses';
import {
  cacheServerScores,
  completedIdsFromStatuses,
  scoresFromStatuses,
} from '../utils/paperAttemptHelpers';

/**
 * What an exam workspace shows of the signed-in user's progress:
 *
 * - `dbPapers` / `statusState`: usePaperAttemptStatuses for these ids.
 * - `scoresMap`: scoresFromStatuses, the server's scores once it answers and
 *   the user's cached copy (`loadScores(userId)`) only while it is on its way.
 * - `completedList`: the ids the server reports completed.
 *
 * Once the server answers, its scores are copied into the user's cache
 * (`saveScore(userId, paperId, score)` for the rows `shouldCache` picks).
 * `cacheTick` is bumped by a retake, which clears the cache, so the pending
 * view rereads it.
 */
export default function useWorkspacePaperProgress(exam, statusRequestKey, {
  userId,
  loadScores,
  saveScore,
  shouldCache,
  cacheTick = 0,
}) {
  const { papers: dbPapers, state: statusState } = usePaperAttemptStatuses(exam, statusRequestKey);

  useEffect(() => {
    if (statusState !== 'ready') return;
    cacheServerScores(dbPapers, (paperId, score) => saveScore(userId, paperId, score), shouldCache);
    // saveScore and shouldCache are module functions: the copy follows the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusState, dbPapers, userId]);

  const scoresMap = useMemo(() => {
    void cacheTick;
    return scoresFromStatuses(statusState, dbPapers, () => loadScores(userId));
    // loadScores is a module function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheTick, statusState, dbPapers, userId]);
  const completedList = useMemo(() => completedIdsFromStatuses(dbPapers), [dbPapers]);

  return { dbPapers, statusState, scoresMap, completedList };
}

/**
 * The signed-in user's workspace target: `load(userId)`, and a setter that
 * stores the new one for that user. Read for the current user on every
 * render after an account switch, so it never shows the previous user's.
 */
export function useUserTarget(load, save, userId) {
  const [stored, setStored] = useState(() => ({ userId, value: load(userId) }));
  const target = stored.userId === userId ? stored.value : load(userId);
  const setTarget = (next) => {
    save(userId, next);
    setStored({ userId, value: next });
  };
  return [target, setTarget];
}
